import type { CommandProps, PasteRule } from '@tiptap/core'
import { Node, nodeInputRule } from '@tiptap/core'
import 'emoji-mart-vue-fast/css/emoji-mart.css'
import { nodePasteRule } from '@/services/forms/tmTiptap/utils/nodePasteRule'
import {
  findEmoji,
  getEmojiImgSrc,
  getEmojiRegexp,
  getEmojiShortnameRegexp,
  getImgAttributes,
} from '@/utils/emojiHelpers'

import {
  EMOJI_COLONS_ATTRIBUTE,
  EMOJI_TEXT_ATTRIBUTE,
  emojiAttributeName,
  HTML_DATA_ATTRIBUTE,
} from '@/services/forms/tmTiptap/const'
import { getChainWithPossibleFocus } from '@/services/forms/tmTiptap/extensions/utils'
import type { Emoji } from '@/services/forms/tmTiptap/types'

const inputRuleHandler = (match: string[]) => {
  const emoji = findEmoji(match[0])
  // this need to show system (e.g. MacOS) emoji
  delete match[1]
  if (emoji) {
    return {
      [EMOJI_TEXT_ATTRIBUTE]: emoji.native,
      [EMOJI_COLONS_ATTRIBUTE]: emoji.colons,
      isText: false,
    }
  }
  const text = match[0]
  // emoji not found - leave the text as is
  delete match[0]
  delete match[1]
  return {
    [EMOJI_TEXT_ATTRIBUTE]: text,
    isText: true,
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emoji: {
      insertEmoji: (emoji: Emoji, focus?: boolean) => ReturnType
    }
  }
}

export const EmojiNode = Node.create({
  name: emojiAttributeName,
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,

  renderText({ node }) {
    return node.attrs[EMOJI_TEXT_ATTRIBUTE]
  },

  addAttributes() {
    return {
      [EMOJI_TEXT_ATTRIBUTE]: {},
      [EMOJI_COLONS_ATTRIBUTE]: {},
      [HTML_DATA_ATTRIBUTE]: {
        default: emojiAttributeName,
      },
      isText: {},
    }
  },

  parseHTML() {
    return [
      {
        tag: `img[${HTML_DATA_ATTRIBUTE}="${this.name}"][${EMOJI_TEXT_ATTRIBUTE}]`,
      },
      {
        tag: `img[${HTML_DATA_ATTRIBUTE}="emailEmoji"][${EMOJI_TEXT_ATTRIBUTE}]`,
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    if (!HTMLAttributes[EMOJI_TEXT_ATTRIBUTE]) {
      return [
        'img',
        {
          ...HTMLAttributes,
          src: getEmojiImgSrc(),
        },
      ]
    }
    const emoji = findEmoji(HTMLAttributes[EMOJI_TEXT_ATTRIBUTE])
    if (!emoji) {
      return ['span', HTMLAttributes, HTMLAttributes[EMOJI_TEXT_ATTRIBUTE]]
    }
    return [
      'img',
      {
        ...HTMLAttributes,
        ...getImgAttributes(emoji),
      },
    ]
  },

  addInputRules() {
    const regexps = [getEmojiShortnameRegexp(true), getEmojiRegexp(true)]
    return regexps.map((regex) =>
      nodeInputRule({
        find: regex,
        type: this.type,
        getAttributes: (match) => inputRuleHandler(match),
      }),
    )
  },

  addPasteRules() {
    const regexps = [getEmojiShortnameRegexp(false, true), getEmojiRegexp(false, true)]
    return regexps.map((regex) =>
      nodePasteRule({
        find: regex,
        type: this.type,
        getAttributes: (match: string[]) => inputRuleHandler(match),
      }),
    ) as unknown as PasteRule[]
  },

  addCommands() {
    return {
      insertEmoji:
        (emoji: Emoji, focus = true) =>
        ({ chain }: CommandProps) =>
          getChainWithPossibleFocus(chain(), focus)
            .insertContent({
              type: emojiAttributeName,
              attrs: {
                [EMOJI_TEXT_ATTRIBUTE]: emoji.native,
                [EMOJI_COLONS_ATTRIBUTE]: emoji.colons,
                isText: false,
              },
            })
            .run(),
    }
  },
})
